import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Upload } from 'lucide-react';
import SourceRow from './SourceRow';
import { ALL_DOC_TYPES, resolveUiState } from './sourceState';
import type {
  ConductRiskDocumentType,
  ConductRiskSourceLink,
  ConductRiskValidationIssue,
  ConductRiskValidationStatus,
} from '../../../api/apiTypesConductRisk';

interface Props {
  validationStatus:      ConductRiskValidationStatus | null;
  issuesByDocumentType:  Partial<Record<ConductRiskDocumentType, ConductRiskValidationIssue[]>>;
  sourceLinks:           ConductRiskSourceLink[];
  replacingDocType:      string | null;
  validationError:       string | null;
  hasMissingLinks:       boolean;
  onOpenAddSources:      () => void;
  onReplace:             (dt: ConductRiskDocumentType) => void;
  onUnlink:              (dt: ConductRiskDocumentType) => void;
}

const SourcesSection = ({
  validationStatus,
  issuesByDocumentType,
  sourceLinks,
  replacingDocType,
  validationError,
  hasMissingLinks,
  onOpenAddSources,
  onReplace,
  onUnlink,
}: Props) => {
  // Chiuso quando validate = 'OK' (tutte le card verdi), aperto altrimenti.
  // Sincronizzato quando validationStatus cambia (es. dopo un refetch).
  const shouldBeOpen = validationStatus !== 'OK';
  const [isOpen, setIsOpen] = useState(shouldBeOpen);
  useEffect(() => { setIsOpen(shouldBeOpen); }, [shouldBeOpen]);

  // "Caricata" = ha un link nel BE (state != 'missing_link'). Include quindi
  // anche gli stati intermedi di parsing (not_run / in_progress / failed):
  // la fonte e' presente, il conteggio non deve escluderla.
  const loadedCount    = ALL_DOC_TYPES.filter(dt => resolveUiState(issuesByDocumentType[dt]) !== 'missing_link').length;
  const parsingCount   = ALL_DOC_TYPES.filter(dt => {
    const s = resolveUiState(issuesByDocumentType[dt]);
    return s === 'parser_not_run' || s === 'parser_in_progress';
  }).length;

  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(v => !v)}
          className="flex items-center gap-2 text-left group"
        >
          <ChevronDown
            className={`w-4 h-4 text-[#1B365D]/60 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`}
          />
          <div>
            <h3 className="text-base font-semibold text-[#1B365D] group-hover:text-[#1B365D]/80">
              Fonti del report
            </h3>
            <p className="text-xs text-gray-400">
              {loadedCount}/{ALL_DOC_TYPES.length} caricate
              {parsingCount > 0 ? ` · ${parsingCount} in parsing` : ''}
              {' '}· stato aggiornato ogni minuto
            </p>
          </div>
        </button>
        {hasMissingLinks && (
          <button
            onClick={onOpenAddSources}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors"
            style={{ backgroundColor: '#1B365D' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#142a4a')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#1B365D')}
          >
            <Upload className="w-3.5 h-3.5" />
            Carica Fonti
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="sources-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            {validationError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2 mb-2">
                {validationError}
              </div>
            )}

            <ul className="space-y-1.5">
              {ALL_DOC_TYPES.map(dt => {
                const state = resolveUiState(issuesByDocumentType[dt]);
                const link = sourceLinks.find(l => l.document_type === dt);
                return (
                  <SourceRow
                    key={dt}
                    docType={dt}
                    state={state}
                    fileName={link?.source?.file_name}
                    hasLink={!!link}
                    isReplacing={replacingDocType === dt}
                    onReplace={onReplace}
                    onUnlink={onUnlink}
                  />
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SourcesSection;
